import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const SITE_URL = 'https://www.stikes-hi.ac.id';
const SITE_NAME = 'STIKES Harapan Ibu Jambi';
const DEFAULT_TITLE = 'STIKES HI | Kampus Kesehatan Unggulan Jambi';
const DEFAULT_DESCRIPTION =
  'STIKES Harapan Ibu Jambi - Sekolah Tinggi Ilmu Kesehatan Unggulan, Berkarakter, dan Berdaya Saing di Jambi';
const DEFAULT_IMAGE = `${SITE_URL}/og-image.jpg`;

const setMeta = (selector, value) => {
  let el = document.querySelector(selector);
  if (!el) {
    el = document.createElement('meta');
    const match = selector.match(/\[([^=]+)="([^"]+)"\]/);
    if (match) {
      el.setAttribute(match[1], match[2]);
      document.head.appendChild(el);
    }
  }
  if (el) el.setAttribute('content', value);
};

const setCanonical = (href) => {
  let el = document.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
};

const setJsonLd = (id, data) => {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
};

const removeJsonLd = (id) => {
  const el = document.getElementById(id);
  if (el) el.remove();
};

/**
 * SEO component — updates <head> tags dynamically for each page.
 *
 * @param {string}  title       - Page title (akan di-append " | STIKES HI" otomatis)
 * @param {string}  description - Meta description (max ~160 karakter)
 * @param {string}  image       - Absolute URL untuk OG image
 * @param {string}  type        - og:type, default "website", gunakan "article" untuk berita
 * @param {object}  article     - Opsional, untuk halaman artikel: { publishedTime, section, author }
 * @param {boolean} noindex     - Set true untuk halaman yang tidak ingin diindex Google
 * @param {array}   breadcrumb  - Opsional: [{ name, path }] untuk JSON-LD BreadcrumbList
 * @param {array}   keywords    - Opsional: array kata kunci untuk meta keywords
 */
const SEO = ({
  title,
  description,
  image,
  type = 'website',
  article = null,
  noindex = false,
  breadcrumb = null,
  keywords = null,
}) => {
  const { pathname } = useLocation();

  const fullTitle = title ? `${title} | STIKES HI` : DEFAULT_TITLE;
  const fullDescription = (description || DEFAULT_DESCRIPTION).replace(/\s+/g, ' ').trim().slice(0, 160);
  const fullImage = image || DEFAULT_IMAGE;
  const canonicalUrl = `${SITE_URL}${pathname}`;

  useEffect(() => {
    document.title = fullTitle;

    setMeta('meta[name="description"]', fullDescription);
    setMeta('meta[name="robots"]', noindex ? 'noindex, nofollow' : 'index, follow');
    if (keywords && keywords.length) {
      const kw = (Array.isArray(keywords) ? keywords : [keywords]).filter(Boolean).join(', ');
      if (kw) setMeta('meta[name="keywords"]', kw);
    }

    // Open Graph
    setMeta('meta[property="og:title"]', fullTitle);
    setMeta('meta[property="og:description"]', fullDescription);
    setMeta('meta[property="og:image"]', fullImage);
    setMeta('meta[property="og:image:alt"]', fullTitle);
    setMeta('meta[property="og:url"]', canonicalUrl);
    setMeta('meta[property="og:type"]', type);
    setMeta('meta[property="og:site_name"]', SITE_NAME);
    setMeta('meta[property="og:locale"]', 'id_ID');

    // Twitter Card
    setMeta('meta[name="twitter:card"]', 'summary_large_image');
    setMeta('meta[name="twitter:title"]', fullTitle);
    setMeta('meta[name="twitter:description"]', fullDescription);
    setMeta('meta[name="twitter:image"]', fullImage);
    setMeta('meta[name="twitter:image:alt"]', fullTitle);
    setMeta('meta[name="twitter:url"]', canonicalUrl);

    // Canonical
    setCanonical(canonicalUrl);

    // Article-specific OG tags
    if (article?.publishedTime) {
      setMeta('meta[property="article:published_time"]', article.publishedTime);
    }
    if (article?.modifiedTime) {
      setMeta('meta[property="article:modified_time"]', article.modifiedTime);
    }
    if (article?.section) {
      setMeta('meta[property="article:section"]', article.section);
    }
    if (article?.author) {
      setMeta('meta[property="article:author"]', article.author);
    }

    // ── JSON-LD: Article / NewsArticle ────────────────────────
    if (type === 'article') {
      setJsonLd('jsonld-article', {
        '@context': 'https://schema.org',
        '@type': 'NewsArticle',
        headline: fullTitle,
        description: fullDescription,
        image: [fullImage],
        datePublished: article?.publishedTime || undefined,
        dateModified: article?.modifiedTime || article?.publishedTime || undefined,
        articleSection: article?.section || undefined,
        keywords: keywords?.length ? keywords.join(', ') : undefined,
        inLanguage: 'id-ID',
        author: article?.author
          ? { '@type': 'Organization', name: article.author }
          : { '@type': 'Organization', name: SITE_NAME },
        publisher: {
          '@type': 'Organization',
          name: SITE_NAME,
          logo: { '@type': 'ImageObject', url: `${SITE_URL}/android-chrome-512x512.png` },
        },
        mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
      });
    } else {
      removeJsonLd('jsonld-article');
    }

    // ── JSON-LD: BreadcrumbList ───────────────────────────────
    if (breadcrumb && breadcrumb.length) {
      const items = [
        { '@type': 'ListItem', position: 1, name: 'Beranda', item: SITE_URL },
        ...breadcrumb.map((crumb, idx) => ({
          '@type': 'ListItem',
          position: idx + 2,
          name: crumb.name,
          item: crumb.path ? `${SITE_URL}${crumb.path}` : canonicalUrl,
        })),
      ];
      setJsonLd('jsonld-breadcrumb', {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: items,
      });
    } else {
      removeJsonLd('jsonld-breadcrumb');
    }

    return () => {
      document.title = DEFAULT_TITLE;
    };
  }, [fullTitle, fullDescription, fullImage, canonicalUrl, type, noindex, article, breadcrumb, keywords]);

  return null;
};

export default SEO;
